import _ from 'lodash';
import { SortingRule } from 'react-table';
import { formatDateForBackend, formatDateTimeForBackend } from '../../services/primitives/date-utils';
// Leaf imports, not the `./components` barrel: the barrel reaches `ui/base`, whose
// `variables/responsive.ts` reads `window.location` at module scope. Going through it would make
// this module — pure request building — impossible to unit test outside a DOM.
import {
    FilterMethod,
    ICustomFilter,
    ICustomFilterAsyncSelect,
    ICustomFilterBoolean,
    ICustomFilterDate,
    ICustomFilterDateRange,
    ICustomFilterDateTime,
    ICustomFilterFullText,
    ICustomFilterMultiString,
    ICustomFilterNumber,
    ICustomFilterPhone,
    ICustomFilterSelect,
    ICustomFilterString,
} from './components/filters/filters.models';
import { isEmptyValueMethod, MULTI_STRING_METHODS, splitMultiValues } from './components/filters/filters.service';
import { ITableColumn } from './data-table.presenter';

export interface ISortRequest {
    sort: string[];
    order: string[];
}

enum ISortOrderEnum {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING',
}

/**
 * Filter mapper
 * Please refer Filter.id to filterData that supports on the server
 */
export function createRequestFilters(filters?: ICustomFilter[], omitActive: boolean = false): Record<string, string[]> {
    if (!filters) {
        return {};
    }

    // Add default active filter if it is manually not specified, unless omitActive is true
    if (!omitActive) {
        filters = withActiveFilter(filters);
    }

    return filters.reduce((acc: Record<string, string[]>, filter) => {
        const emptyValueMethod = filter.filterMethod && isEmptyValueMethod(filter.filterMethod);

        if (!filter.filterId || !filter.filterMethod || (!filter.value && !emptyValueMethod)) {
            return acc;
        }

        // A non-empty value can still parse to nothing — a MultiString holding only separators, a
        // DateRange "between" without both dates (see parseFilterDateRange). Sending those would be a
        // malformed filter, so drop them here rather than asking every caller to pre-validate.
        const parsed = parseFilter(filter);
        if (!parsed && !emptyValueMethod) {
            return acc;
        }

        // If we already have filter with this id, add more to array
        if (acc[filter.filterId]) {
            acc[filter.filterId].push(parsed);
        } else {
            acc[filter.filterId] = [parsed];
        }

        return acc;
    }, {});
}

/**
 * Filter mapper
 * Please refer Filter.id to filterData that supports on the server
 */
export function createGroupRequestFilters(
    filterGroups?: ICustomFilter[][],
    omitActive: boolean = false,
): Record<string, string[]> {
    if (!filterGroups) {
        return {};
    }

    // Add default active filter if it is manually not specified, unless omitActive is true
    if (!omitActive) {
        filterGroups = withGroupActiveFilter(filterGroups);
    }

    return filterGroups
        .map<Record<string, string[]>>(group => {
            return group.reduce((acc, filter) => {
                if (!filter.filterId || !filter.filterMethod || !filter.value) {
                    return acc;
                }
                const parsed = parseFilter(filter);
                if (!parsed) {
                    return acc;
                }
                if (acc[filter.filterId]) {
                    acc[filter.filterId][0] += `,${parsed}`;
                } else {
                    acc[filter.filterId] = [parsed];
                }
                return acc;
            }, {});
        })
        .reduce<Record<string, string[]>>((acc, el) => {
            return _.merge(el, acc);
        }, {});
}

function withActiveFilter(filters: ICustomFilter[]): ICustomFilter[] {
    const isActiveExists = filters.find(filter => filter.filterId === 'filterActive');

    if (!isActiveExists) {
        filters.push({
            filterId: 'filterActive',
            filterMethod: FilterMethod.EQ,
            filterType: 'String',
            value: '1',
        });
    }

    return filters;
}

function withGroupActiveFilter(filters: ICustomFilter[][]): ICustomFilter[][] {
    const flatten = _.flatten(filters);
    const isActiveExists = flatten.find(filter => filter.filterId === 'filterActive');

    if (!isActiveExists) {
        filters.push([
            {
                filterId: 'filterActive',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: '1',
            },
        ]);
    }

    return filters;
}

/**
 * Flatten specified keys in filters map (as returned by createRequestFilters) by using first array value.
 */
export function flattenRequestFilters(filters: Record<string, string[] | string> | undefined, keys: string[]): void {
    if (filters) {
        _.forEach(keys, key => {
            const f = _.get(filters, key);
            if (f) {
                _.set(filters, key, _.first(f));
            }
        });
    }
}

/**
 * Fingerprint of `ICustomFilter.value` for cache keys (not the API wire format).
 * Distinguishes Date / DateRange tuples and multi-select arrays that would otherwise
 * collapse to undefined and collide in `baseFiltersKey`.
 */
export function serializeCustomFilterValue(value: unknown): string | undefined {
    if (value == null) {
        return undefined;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value.map(item => serializeCustomFilterValue(item) ?? '').join(',');
    }
    if (typeof value === 'object' && 'value' in value) {
        return serializeCustomFilterValue((value as { value?: unknown }).value);
    }
    return undefined;
}

export function parseFilter(filter: ICustomFilter): string {
    switch (filter.filterType) {
        case 'Number':
            return parseFilterNumber(filter);
        case 'String':
            return parseFilterString(filter);
        case 'MultiString':
            return parseFilterMultiString(filter);
        case 'FullText':
            return parseFilterFullText(filter);
        case 'Phone':
            return parseFilterPhone(filter);
        case 'Date':
            return parseFilterDate(filter);
        case 'DateTime':
            return parseFilterDateTime(filter);
        case 'DateRange':
            return parseFilterDateRange(filter);
        case 'Boolean':
            return parseFilterBoolean(filter);
        case 'AsyncSelect':
        case 'Select':
        default:
            return parseFilterSelect(filter);
        // NB: TS error for <never> type since switch covers all values
        // default:
        //      throw new Error(`No rule for parsing ${filter.filterType} type!`);
    }
}


function formatFilterString(method: FilterMethod, value: string = ''): string {
    return `${method}:${value}`;
}

function parseFilterString(filter: ICustomFilterString): string {
    return formatFilterString(filter.filterMethod, filter.value);
}

/**
 * Many values for one field, as a single comma-joined condition the API reads as OR:
 * `EQ:a,EQ:b,EQ:c`.
 *
 * Note the method is repeated per value rather than applied once to a joined list — a bare
 * `filterId=a,b` is rejected 400 INVALID_REQUEST (measured on `GET /provider/claim`).
 *
 * `isFilterValid` keeps non-OR-safe methods from getting this far. This throw is the backstop for
 * any path that bypasses it: `NQ:a,NQ:b` matched 971 of 971 claims on sandbox, so sending it would
 * return the whole table under a filter that looks applied. A loud failure beats that.
 */
function parseFilterMultiString(filter: ICustomFilterMultiString): string {
    if (!MULTI_STRING_METHODS.includes(filter.filterMethod)) {
        throw new Error(
            `A multi-value filter cannot be sent with ${filter.filterMethod}: the API ORs the values, ` +
                'so a negation would match every row.',
        );
    }

    return splitMultiValues(filter.value)
        .map(value => formatFilterString(filter.filterMethod, value))
        .join(',');
}

function parseFilterFullText(filter: ICustomFilterFullText): string {
    if (!filter.value) {
        return '';
    }

    let value = '';

    switch (filter.filterMethod) {
        case 'OW':
            // 'apple'
            value = filter.value;
            break;
        case 'CT':
            // '+apple +orange'
            value = filter.value
                .split(' ')
                .map(el => `+${el}`)
                .join(' ');
            break;
        case 'NCT':
            // '-apple -orange'
            value = filter.value
                .split(' ')
                .map(el => `-${el}`)
                .join(' ');
            break;
        case 'EX':
            // '"apple orange"'
            value = `"${filter.value}"`;
            break;
        case 'FT':
        default:
            // unformatted fulltext
            value = filter.value;
            break;
    }

    return `FTS:${value}`;
}

function parseFilterPhone(filter: ICustomFilterPhone): string {
    return formatFilterString(filter.filterMethod, filter.value);
}

function parseFilterBoolean(filter: ICustomFilterBoolean): string {
    const value: boolean | string = _.get(filter, 'value.value', ''); // Get select option value
    return formatFilterString(filter.filterMethod, Number(value).toString()); // true = '1', false = '0'
}

function parseFilterNumber(filter: ICustomFilterNumber): string {
    return formatFilterString(filter.filterMethod, filter.value);
}

function parseFilterDate(filter: ICustomFilterDate): string {
    const value = filter.value ? formatDateForBackend(filter.value) : '';

    return formatFilterString(filter.filterMethod, value); // i.e. EQ:2019-08-07 or EMPTY
}

function parseFilterDateTime(filter: ICustomFilterDateTime): string {
    if (!filter.value) {
        return formatFilterString(filter.filterMethod, '');
    }

    // Special handling for "on" filter - convert to date range covering entire day
    if (filter.filterMethod === FilterMethod.EQ) {
        // User selected "on Nov 25, 2025" - match entire day in their timezone
        const startDate = new Date(filter.value);
        startDate.setHours(0, 0, 0, 0); // Start of day in local timezone
        
        const endDate = new Date(filter.value);
        endDate.setHours(23, 59, 59, 999); // End of day in local timezone
        
        // Convert to UTC and use BETWEEN filter
        const startUTC = startDate.toISOString();
        const endUTC = endDate.toISOString();
        
        return `${FilterMethod.BT}:${startUTC}:${endUTC}`;
    }

    // For other filter methods, use the original datetime
    const value = formatDateTimeForBackend(filter.value);
    return formatFilterString(filter.filterMethod, value);
}

function parseFilterDateRange(filter: ICustomFilterDateRange): string {
    if (!Array.isArray(filter.value)) {
        // In case of undefined
        return '';
    }

    const dates = filter.value.map(d => formatDateForBackend(d)).filter(Boolean);

    // "Between" needs both ends. With zero or one date this used to emit `BT:` or `BT:2025-11-20` —
    // non-empty, so the empty-value guard in the request builders let it through to the API. Returning
    // '' here is what lets that guard drop it. Other methods are unchanged.
    if (filter.filterMethod === FilterMethod.BT && dates.length !== 2) {
        return '';
    }

    return formatFilterString(filter.filterMethod, dates.join(':')); // i.e. BT:2025-11-20:2025-11-25 or EMPTY
}

function parseFilterSelect(filter: ICustomFilterSelect | ICustomFilterAsyncSelect | ICustomFilterBoolean): string {
    const value = _.get(filter, 'value.value', ''); // Get select option value
    return formatFilterString(filter.filterMethod, String(value));
}

export function parseSorting2<D extends object = {}>(
    sorted: SortingRule<object>[],
    columns: ITableColumn<D>[] | undefined,
): ISortRequest {
    const emptySorting: ISortRequest = {
        sort: [],
        order: [],
    };

    if (!columns) {
        // We should never pass undefined instead of columns array, but return nothing in case if we accidentaly did that.
        return emptySorting;
    }

    const sortRequest = sorted.reduce((acc: ISortRequest, sort) => {
        // Find column associated to the sorting.
        const column = _.find(columns, col => col.id === sort.id);
        if (!column) {
            return acc;
        }

        // Select sortType if it is exists.
        const sortType = column.sortType || column.id;
        if (!sortType) {
            return acc;
        }

        const order = sort.desc ? ISortOrderEnum.DESCENDING : ISortOrderEnum.ASCENDING;

        acc.sort.push(sortType);
        acc.order.push(order);

        return acc;
    }, emptySorting);

    if (sortRequest.sort.length !== sortRequest.order.length) {
        throw new Error('There should be the same amount of sort and orders');
    }

    return sortRequest;
}

// TODO(dmitry): move it to independent StorageService.
export class TableStorage {
    public static saveItem<T extends object = {}>(bucket: string, name: string, item: T) {
        const storageName = `table_${bucket}_${name}`;
        window.localStorage.setItem(storageName, JSON.stringify(item));
    }

    public static getItem<T extends object = {}>(bucket: string, name: string): T | undefined {
        const storageName = `table_${bucket}_${name}`;
        const storageFilters = window.localStorage.getItem(storageName);
        if (storageFilters) {
            return JSON.parse(storageFilters);
        }
    }

    public static clear(pattern: RegExp) {
        const keys = Object.keys(window.localStorage);
        keys.forEach(key => {
            if (pattern.test(key)) {
                window.localStorage.removeItem(key);
            }
        });
    }
}

export function normalizeStorageFilters(filters: ICustomFilter[]): ICustomFilter[] {
    return filters.map(filter => {
        switch (filter.filterType) {
            case 'DateRange':
                return { ...filter, value: _.map(filter.value, el => new Date(el)) };
            case 'Date':
            case 'DateTime':
                return { ...filter, value: filter.value ? new Date(filter.value) : filter.value };
            default:
                return filter;
        }
    }) as ICustomFilter[];
}
